import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, useNavigation, useRouteLoaderData, useSearchParams } from '@remix-run/react';
import { normalizeEmail } from '@shared/dream-components';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import useEmail from '~/hooks/useEmail';
import type { Community } from '~/interfaces/community';
import type { RootLoaderData } from '~/root';
import type { loader as communityLoader } from '~/routes/community';
import logout from '~/utils/logout';

import { CommunityTermsConsent } from './ui/CommunityTermsConsent';
import { PillButton, pillButtonInteraction } from './ui/PillButton';
import { SignedInAs } from './ui/SignedInAs';

import { useCommunityJoinGate } from '../_hooks/useCommunityJoinGate';
import { useCommunityMembership } from '../_hooks/useCommunityMembership';
import { getJoinRefusalMessage } from '../_utils/getJoinRefusalMessage';
import type { CommunityInviteView } from '../_utils/resolveCommunityInviteView.server';
import { AwaitingAccessStep } from './BaseUpgradeModal/_components/AwaitingAccessStep';
import { pollAccessRoute } from './BaseUpgradeModal/useAccessPoll';
import { CommunityAuthModal } from './CommunityAuthModal';
import { OwnerGateButton } from './OwnerGateButton';

const checkCommunityAccess = pollAccessRoute('/community_access', 'community_accessible');
// The poll revalidates the layout on its own; a joinable gate is all this modal needs.
const noop = () => {};

interface CommunityInviteModalProps {
  invite: CommunityInviteView;
  publicationName: string;
}

// Route-owned so it renders on first paint over the community shell. Every dismissal
// logs the subscriber out and reloads clean /community. Without access to a gated
// community, "See plans" opens the upgrade modal and this one steps aside until the
// purchase returns here; the tier lands through the Stripe webhook, so the return
// polls for access before the accept form takes over.
export const CommunityInviteModal: FC<CommunityInviteModalProps> = ({ invite, publicationName }) => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const [isDismissing, setIsDismissing] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const { currentModal } = useGlobalModals();
  const community = useRouteLoaderData<typeof communityLoader>('routes/community')?.community;
  const { openJoinModal } = useCommunityMembership();
  const [, setEmail] = useEmail();
  const access = useInviteAccess(invite);

  const dismiss = () => {
    setIsDismissing(true);
    logout('token');
  };

  // The checkout starts from the email useEmail remembers, so store the invited address
  // before the upgrade modal mounts; the invitee can still edit it there.
  const seePlans = (email: string) => {
    setEmail(email);
    openJoinModal();
  };

  const copy = getInviteCopy(invite, access, t, community);
  const isSubmitting = navigation.state !== 'idle' || isDismissing;
  const isSteppingAside = currentModal === 'community_join' || currentModal === 'community_login';

  return (
    <CommunityAuthModal
      isOpen={!isSteppingAside}
      onClose={dismiss}
      title={copy.title}
      description={copy.description}
      closeLabel={t('shared.close')}
      publicationName={publicationName}
      disableInitialTransition
      footer={access === 'refused' ? <SignedInAs className="text-center text-black/50" /> : undefined}
    >
      {access === 'wrong_account' && (
        <PillButton
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          onClick={dismiss}
          disabled={isSubmitting}
          className={pillButtonInteraction}
        >
          {t('accept_community_invite.wrong_account.cta')}
        </PillButton>
      )}
      {invite.state === 'accept_ready' && access === 'accept' && (
        <Form method="post" action="/community-join" className="flex w-full flex-col gap-4">
          <input type="hidden" name="t" value={invite.tokenId} />
          <input type="hidden" name="token" value={invite.token} />
          <CommunityTermsConsent checked={termsAccepted} onChange={setTermsAccepted} />
          <PillButton
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={isSubmitting || !termsAccepted}
            className={pillButtonInteraction}
          >
            {navigation.state !== 'idle' ? t('accept_community_invite.accepting') : t('accept_community_invite.accept')}
          </PillButton>
        </Form>
      )}
      {access === 'refused' && <OwnerGateButton gate={community?.gate} size="lg" fullWidth />}
      {access === 'awaiting_access' && <AwaitingAccessStep onAccessGranted={noop} checkAccess={checkCommunityAccess} />}
      {invite.state === 'accept_ready' && access === 'upgrade' && (
        <PillButton
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          onClick={() => seePlans(invite.email)}
          disabled={isSubmitting}
          className={pillButtonInteraction}
        >
          {t('accept_community_invite.paid.cta')}
        </PillButton>
      )}
      {invite.state === 'already_accepted' && (
        <PillButton
          variant="primary"
          size="lg"
          fullWidth
          onClick={dismiss}
          disabled={isSubmitting}
          className={pillButtonInteraction}
        >
          {t('accept_community_invite.already_accepted.cta')}
        </PillButton>
      )}
    </CommunityAuthModal>
  );
};

type InviteAccess = 'accept' | 'upgrade' | 'closed' | 'refused' | 'awaiting_access' | 'wrong_account';

// What an accept_ready invite can do for the current viewer. Precedence: the session
// identity first (no gate can fix a wrong account), then the post-purchase wait, then
// the gate itself. Null for invites with nothing left to do.
function useInviteAccess(invite: CommunityInviteView): InviteAccess | null {
  const gate = useCommunityJoinGate();
  const [searchParams] = useSearchParams();
  const rootData = useRouteLoaderData('root') as RootLoaderData | undefined;
  const isLoggedIn = Boolean(rootData?.isLoggedIn);
  const sessionEmail = isLoggedIn ? rootData?.subscription?.email : undefined;

  if (invite.state !== 'accept_ready') return null;

  if (sessionEmail !== undefined && normalizeEmail(sessionEmail) !== normalizeEmail(invite.email)) {
    return 'wrong_account';
  }
  if (gate === 'upgrade' && isLoggedIn && searchParams.get('upgrade_return') === '1') {
    return 'awaiting_access';
  }
  return gate === 'joinable' ? 'accept' : gate;
}

type TFn = ReturnType<typeof useTranslation>['t'];

function getInviteCopy(
  invite: CommunityInviteView,
  access: InviteAccess | null,
  t: TFn,
  community: Pick<Community, 'gate'> | null | undefined,
): { title: string; description: string } {
  switch (invite.state) {
    case 'accept_ready':
      switch (access) {
        case 'wrong_account':
          return {
            title: t('accept_community_invite.title'),
            description: t('accept_community_invite.wrong_account.body'),
          };
        case 'closed':
          return {
            title: t('community.paid_gate.closed_title'),
            description: t('community.paid_gate.closed_description'),
          };
        case 'refused':
          return { title: invite.communityName, description: getJoinRefusalMessage(community, t) };
        case 'upgrade':
          return {
            title: t('accept_community_invite.title'),
            description: t('accept_community_invite.paid.body', { communityName: invite.communityName }),
          };
        default:
          return {
            title: t('accept_community_invite.title'),
            description: t('accept_community_invite.body', { communityName: invite.communityName }),
          };
      }
    case 'already_accepted':
      return {
        title: t('accept_community_invite.already_accepted.title'),
        description: t('accept_community_invite.already_accepted.body'),
      };
    case 'invalid_token':
      return {
        title: t('accept_community_invite.invalid_token.title'),
        description: t('accept_community_invite.invalid_token.body'),
      };
  }
}
